/**
 * Scenario Classification & Change Reasons
 *
 * Pure helpers that classify an estate into a UX state and explain
 * why the scenario has complexity or has changed from a simple baseline.
 *
 * These are UX-only classifications — they do NOT alter tax calculations.
 */

import type { IHTCalculation, EstateAssets, EstateProfile } from '../../types';

// ── Classification Types ─────────────────────────────────────────────

export type ScenarioClass =
    | 'SIMPLE_EXCEPTED'
    | 'EXCEPTED_WITH_COMPLEX_FEATURES'
    | 'TAXABLE'
    | 'COMPLEX_REVIEW_RECOMMENDED';

export type ScenarioReason = {
    id: string;
    priority: number;       // lower = more important
    message: string;
    shortMessage: string;   // for inline hints
};

export type ScenarioResult = {
    classification: ScenarioClass;
    label: string;
    description: string;
    reasons: ScenarioReason[];
};

// ── Labels & Descriptions ────────────────────────────────────────────

const LABELS: Record<ScenarioClass, string> = {
    SIMPLE_EXCEPTED: 'Simple excepted estate',
    EXCEPTED_WITH_COMPLEX_FEATURES: 'Excepted estate with added complexity',
    TAXABLE: 'Taxable estate',
    COMPLEX_REVIEW_RECOMMENDED: 'Complex estate — review recommended',
};

const DESCRIPTIONS: Record<ScenarioClass, string> = {
    SIMPLE_EXCEPTED:
        'Based on the information entered, this appears to be a simple estate with no inheritance tax due.',
    EXCEPTED_WITH_COMPLEX_FEATURES:
        'No inheritance tax is currently estimated, but this estate includes features that make it less straightforward.',
    TAXABLE:
        'Based on the information entered, inheritance tax is likely due.',
    COMPLEX_REVIEW_RECOMMENDED:
        'This estate includes features that make the result more sensitive to legal and tax detail. Professional review is recommended.',
};

// ── Complexity Flag Detection ────────────────────────────────────────

export interface ComplexityFlags {
    hasTaxDue: boolean;
    isConfirmedResult: boolean;  // true when confidence.overall === 'confirmed' (no assumptions)
    hasLifetimeGifts: boolean;
    hasChargeableGifts: boolean;
    hasTransferredNrb: boolean;
    hasTransferredRnrb: boolean;
    hasSpouseExemption: boolean;
    hasRnrbTaper: boolean;
    hasNoRnrb: boolean;
    estateAbove2M: boolean;
    hasBpr: boolean;
    hasApr: boolean;
    hasTrusts: boolean;
    hasUncertainAnswers: boolean;
    hasDownsizing: boolean;
    hasCharitableBequest: boolean;
    hasSignificantGifts: boolean;
    hasForeignAssets: boolean;
}

export function detectComplexityFlags(
    ihtCalc: IHTCalculation | null | undefined,
    assets: EstateAssets | null | undefined,
    profile: EstateProfile | null | undefined,
): ComplexityFlags {
    const bd = ihtCalc?.breakdown;
    const reliefs = bd?.reliefs;
    const ihtDetails = (profile as any)?.iht_details;

    const totalTaxDue = ihtCalc?.total_tax_due ?? 0;
    const grossValue = bd?.gross_value ?? 0;
    const giftsTotal = bd?.gifts_total_within_7y ?? 0;
    const chargeableGifts = bd?.gifts_chargeable_total ?? 0;
    const spouseExemption = reliefs?.spouse_exemption?.amount ?? bd?.spouse_exemption_total ?? 0;
    const bprTotal = reliefs?.bpr?.total ?? 0;
    const aprTotal = reliefs?.apr?.total ?? 0;
    const tnrbPercent = reliefs?.tnrb?.transferred_percent ?? 0;
    const trnrbPercent = reliefs?.trnrb?.transferred_percent ?? 0;
    const taperApplied = reliefs?.trnrb?.taper_applied ?? false;
    const rnrbAvailable = bd?.rnrb_available ?? 0;
    const downsizingAmount = reliefs?.downsizing?.amount ?? 0;
    const charitableAmount = reliefs?.charity_exemption?.amount ?? 0;

    // Trust screening
    const ts = ihtDetails?.trust_screening;
    const hasTrusts = !!(
        ts?.trust_involvement_detected ||
        ts?.gwr_possible ||
        ts?.gwr_likely ||
        ts?.specialist_review_needed ||
        assets?.trusts_or_life_interest?.exists
    );

    // Uncertain answers
    const hasUncertainAnswers = !!(
        ts?.trust_involvement_uncertain ||
        ts?.uncertain ||
        ihtCalc?.confidence?.overall === 'incomplete'
    );

    // Gifts
    const lifetimeGifts = assets?.lifetime_gifts ?? [];
    const hasLifetimeGifts = lifetimeGifts.length > 0 || giftsTotal > 0;
    const hasSignificantGifts = giftsTotal > 50_000 || chargeableGifts > 0;

    // RNRB: detect if no RNRB is available when one might be expected
    const hasNoRnrb = rnrbAvailable === 0 && grossValue > 325_000;

    // Foreign assets
    const hasForeignAssets = !!(assets?.foreign_assets?.exists);

    return {
        hasTaxDue: totalTaxDue > 0,
        isConfirmedResult: ihtCalc?.confidence?.overall === 'confirmed',
        hasLifetimeGifts,
        hasChargeableGifts: chargeableGifts > 0,
        hasTransferredNrb: tnrbPercent > 0,
        hasTransferredRnrb: trnrbPercent > 0,
        hasSpouseExemption: spouseExemption > 0,
        hasRnrbTaper: taperApplied,
        hasNoRnrb,
        estateAbove2M: grossValue > 2_000_000,
        hasBpr: bprTotal > 0,
        hasApr: aprTotal > 0,
        hasTrusts,
        hasUncertainAnswers,
        hasDownsizing: downsizingAmount > 0,
        hasCharitableBequest: charitableAmount > 0,
        hasSignificantGifts,
        hasForeignAssets,
    };
}

// ── Classification ───────────────────────────────────────────────────

export function classifyScenario(flags: ComplexityFlags): ScenarioClass {
    const complexityMarkers = [
        flags.estateAbove2M,
        flags.hasBpr,
        flags.hasApr,
        flags.hasTrusts,
        flags.hasUncertainAnswers,
        flags.hasSignificantGifts,
        flags.hasForeignAssets,
    ];
    const highComplexity = complexityMarkers.filter(Boolean).length >= 2;

    // COMPLEX_REVIEW_RECOMMENDED: high complexity regardless of tax outcome
    if (flags.estateAbove2M && (flags.hasTrusts || flags.hasUncertainAnswers)) {
        return 'COMPLEX_REVIEW_RECOMMENDED';
    }
    if (flags.hasTrusts && flags.hasUncertainAnswers) {
        return 'COMPLEX_REVIEW_RECOMMENDED';
    }
    if (highComplexity && (flags.hasTaxDue || flags.estateAbove2M)) {
        return 'COMPLEX_REVIEW_RECOMMENDED';
    }

    // TAXABLE: IHT is due
    if (flags.hasTaxDue) {
        return 'TAXABLE';
    }

    // Check for any complexity features
    const anyComplexity =
        flags.hasLifetimeGifts ||
        flags.hasTransferredNrb ||
        flags.hasTransferredRnrb ||
        flags.hasSpouseExemption ||
        flags.hasRnrbTaper ||
        flags.hasDownsizing ||
        flags.hasBpr ||
        flags.hasApr ||
        flags.hasCharitableBequest ||
        flags.hasTrusts ||
        flags.hasForeignAssets ||
        flags.estateAbove2M;

    if (anyComplexity) {
        return 'EXCEPTED_WITH_COMPLEX_FEATURES';
    }

    return 'SIMPLE_EXCEPTED';
}

// ── Reason Generation ────────────────────────────────────────────────

export function getScenarioReasons(flags: ComplexityFlags): ScenarioReason[] {
    const reasons: ScenarioReason[] = [];

    if (flags.hasTaxDue) {
        reasons.push({
            id: 'tax_due',
            priority: 1,
            message: flags.isConfirmedResult
                ? 'Inheritance tax is due on this estate.'
                : 'Inheritance tax is estimated to be due on this estate.',
            shortMessage: 'IHT is due on this estate.',
        });
    }

    if (flags.estateAbove2M) {
        reasons.push({
            id: 'above_2m',
            priority: 2,
            message: 'The estate is above £2 million, which can reduce or remove the residence nil-rate band.',
            shortMessage: 'Estate above £2M may reduce RNRB.',
        });
    }

    if (flags.hasRnrbTaper) {
        reasons.push({
            id: 'rnrb_taper',
            priority: 3,
            message: 'The residence nil-rate band has been tapered due to the estate value exceeding £2 million.',
            shortMessage: 'RNRB tapered due to estate value.',
        });
    }

    if (flags.hasNoRnrb && !flags.hasRnrbTaper) {
        reasons.push({
            id: 'no_rnrb',
            priority: 3,
            message: 'The property is not passing in a way that qualifies for the residence nil-rate band.',
            shortMessage: 'RNRB not available for this property.',
        });
    }

    if (flags.hasChargeableGifts) {
        reasons.push({
            id: 'chargeable_gifts',
            priority: 4,
            message: 'Lifetime gifts within 7 years reduce the available nil-rate band.',
            shortMessage: 'Gifts within 7 years reduce NRB.',
        });
    } else if (flags.hasLifetimeGifts) {
        reasons.push({
            id: 'lifetime_gifts',
            priority: 5,
            message: 'Lifetime gifts have been recorded. These are assessed against the 7-year rule and annual exemptions.',
            shortMessage: 'Lifetime gifts are being assessed.',
        });
    }

    if (flags.hasBpr) {
        reasons.push({
            id: 'bpr',
            priority: 6,
            message: 'Business property relief has been claimed, reducing the chargeable estate.',
            shortMessage: 'BPR claimed.',
        });
    }

    if (flags.hasApr) {
        reasons.push({
            id: 'apr',
            priority: 6,
            message: 'Agricultural property relief has been claimed, reducing the chargeable estate.',
            shortMessage: 'APR claimed.',
        });
    }

    if (flags.hasTransferredNrb) {
        reasons.push({
            id: 'transferred_nrb',
            priority: 7,
            message: 'Transferred nil-rate band has been claimed from a predeceased spouse or civil partner.',
            shortMessage: 'Transferred NRB claimed.',
        });
    }

    if (flags.hasTransferredRnrb) {
        reasons.push({
            id: 'transferred_rnrb',
            priority: 7,
            message: 'Transferred residence nil-rate band has been claimed from a predeceased spouse or civil partner.',
            shortMessage: 'Transferred RNRB claimed.',
        });
    }

    if (flags.hasSpouseExemption) {
        reasons.push({
            id: 'spouse_exemption',
            priority: 8,
            message: 'Spouse or civil partner exemption is being applied.',
            shortMessage: 'Spouse exemption applied.',
        });
    }

    if (flags.hasCharitableBequest) {
        reasons.push({
            id: 'charitable_bequest',
            priority: 8,
            message: 'Charitable bequests have been declared, which may reduce the tax rate.',
            shortMessage: 'Charitable bequest declared.',
        });
    }

    if (flags.hasDownsizing) {
        reasons.push({
            id: 'downsizing',
            priority: 8,
            message: 'Downsizing allowance is being applied for a previously sold qualifying residence.',
            shortMessage: 'Downsizing allowance applied.',
        });
    }

    if (flags.hasTrusts) {
        reasons.push({
            id: 'trusts',
            priority: 5,
            message: 'Trust or gift-with-reservation involvement has been detected. This may require specialist review.',
            shortMessage: 'Trust involvement detected.',
        });
    }

    if (flags.hasForeignAssets) {
        reasons.push({
            id: 'foreign_assets',
            priority: 6,
            message: 'Foreign assets are included in the estate, which may add complexity.',
            shortMessage: 'Foreign assets included.',
        });
    }

    if (flags.hasUncertainAnswers) {
        reasons.push({
            id: 'uncertain',
            priority: 9,
            message: 'Some answers indicate this estate may need professional review.',
            shortMessage: 'Some answers are uncertain.',
        });
    }

    return reasons.sort((a, b) => a.priority - b.priority);
}

// ── Main Entry Point ─────────────────────────────────────────────────

export function classifyEstate(
    ihtCalc: IHTCalculation | null | undefined,
    assets: EstateAssets | null | undefined,
    profile: EstateProfile | null | undefined,
): ScenarioResult {
    const flags = detectComplexityFlags(ihtCalc, assets, profile);
    const classification = classifyScenario(flags);
    const reasons = getScenarioReasons(flags);

    return {
        classification,
        label: LABELS[classification],
        description: DESCRIPTIONS[classification],
        reasons,
    };
}

// ── Test Scenario Mismatch Detection ─────────────────────────────────

export type ScenarioMismatch = {
    message: string;
    reasons: string[];
};

/**
 * Detects when a named test scenario's assumptions no longer hold.
 * Returns null if no mismatch, or a mismatch object with reasons.
 */
export function detectScenarioMismatch(
    originalTags: string[],
    originalName: string,
    flags: ComplexityFlags,
): ScenarioMismatch | null {
    const isOriginallySimple =
        originalTags.includes('no-tax') ||
        originalTags.includes('excepted') ||
        originalTags.includes('basic') ||
        originalName.toLowerCase().includes('simple excepted');

    if (!isOriginallySimple) return null;

    const reasons: string[] = [];

    if (flags.hasTaxDue) {
        reasons.push(flags.isConfirmedResult
            ? 'Inheritance tax is now due.'
            : 'Inheritance tax is now estimated to be due.');
    }
    if (flags.estateAbove2M) {
        reasons.push('Estate value now exceeds £2 million.');
    }
    if (flags.hasLifetimeGifts) {
        reasons.push('Lifetime gifts have been added.');
    }
    if (flags.hasNoRnrb || flags.hasRnrbTaper) {
        reasons.push('The residence nil-rate band has been reduced or removed.');
    }
    if (flags.hasTransferredNrb || flags.hasTransferredRnrb) {
        reasons.push('Transferred allowances have been claimed.');
    }
    if (flags.hasBpr || flags.hasApr) {
        reasons.push('Relief claims have been added.');
    }
    if (flags.hasSpouseExemption) {
        reasons.push('Spouse exemption is being applied.');
    }
    if (flags.hasTrusts) {
        reasons.push('Trust involvement has been introduced.');
    }

    if (reasons.length === 0) return null;

    return {
        message: 'This case no longer matches the original scenario assumptions.',
        reasons,
    };
}
